import { BaseClient } from '../base-client';
import type {
  Announcement,
  CreateAnnouncementInput,
  ListAnnouncementsQuery,
  PatchAnnouncementInput,
} from './types';

type One = { data: Announcement };
type ListResponse = {
  data: Announcement[];
  meta: { total: number; limit: number; offset: number };
};

export class AnnouncementsClient extends BaseClient {
  list(query: ListAnnouncementsQuery = {}): Promise<ListResponse> {
    return this.request('/api/announcements', { query: normalize(query) });
  }

  /** Batch fetch announcements by exactly the referenced ids. See `BaseClient.findByIdsAt`. */
  findByIds(ids: readonly string[]): Promise<ListResponse> {
    return this.findByIdsAt<Announcement>('/api/announcements/find', ids);
  }

  /** Currently-live banners for the all-users feed (`announcement.read_live`). */
  live(): Promise<{ data: Announcement[] }> {
    return this.request('/api/announcements/live');
  }

  get(id: string): Promise<One> {
    return this.request(`/api/announcements/${encodeURIComponent(id)}`);
  }

  create(input: CreateAnnouncementInput): Promise<One> {
    return this.request('/api/announcements', { method: 'POST', body: input });
  }

  patch(id: string, input: PatchAnnouncementInput): Promise<One> {
    return this.request(`/api/announcements/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    });
  }

  remove(id: string): Promise<One> {
    return this.request(`/api/announcements/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: {},
    });
  }

  restore(id: string): Promise<One> {
    return this.request(`/api/announcements/${encodeURIComponent(id)}/restore`, {
      method: 'POST',
      body: {},
    });
  }
}

function normalize(q: object): Record<string, string | number | boolean | undefined> {
  const out: Record<string, string | number | boolean | undefined> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === '') continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    }
  }
  return out;
}
